import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { AiOutlinePlus, AiOutlineDelete } from "react-icons/ai";
import InputComponent from "../InputComponent/InputComponent";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";

const ManualCouponForm = ({ onSubmit, onCancel, isLoading = false }) => {
  const [formData, setFormData] = useState({
    brand_product_code: "",
    brand_name: "",
    brand_type: "",
    redemption_type: "",
    online_redemption_url: "",
    brand_image: "",
    min_redemption_amount: "",
    max_redemption_amount: "",
    category: "",
    descriptions: "",
    tnc: "",
    important_instruction: "",
    redeem_steps: "",
    stock_available: true,
    denomination_list: "",
    denominations: [{ denomination: "", value: 0 }]
  });

  const [errors, setErrors] = useState({});

  // Upload hook for single file (image or csv)
  const [uploadImage, { data: uploadImageData, isLoading: uploadImageIsLoading, error: uploadImageError }] = useUploadSingleFileMutation();
  const [localPreview, setLocalPreview] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const fileName = file.name || "";
    const lower = fileName.toLowerCase();
    const isJpeg = file.type === "image/jpeg" || lower.endsWith(".jpg") || lower.endsWith(".jpeg");
    const isCsv = lower.endsWith(".csv") || file.type === "text/csv" || file.type === "application/vnd.ms-excel";

    if (!isJpeg && !isCsv) {
      toast.error("Only JPEG or CSV files are allowed");
      return;
    }

    // show a local preview for images, filename for csv
    if (isJpeg) {
      const preview = URL.createObjectURL(file);
      setLocalPreview(preview);
      setFormData(prev => ({ ...prev, brand_image: preview }));
    } else {
      setFormData(prev => ({ ...prev, brand_image: fileName }));
    }

    const form = new FormData();
    form.append("image", file);

    // trigger upload (API expects { body: FormData } like other components)
    uploadImage({ body: form });
  };

  useEffect(() => {
    // handle upload states
    toast.dismiss();
    if (uploadImageIsLoading) {
      toast.loading("Uploading file...", { id: "uploadFileLoading" });
    }

    if (uploadImageData) {
      const fileLink = uploadImageData?.body?.fileLink || uploadImageData?.body?.file_link || "";
      if (fileLink) {
        setFormData(prev => ({ ...prev, brand_image: fileLink }));
        toast.success("File uploaded successfully", { id: "uploadFileSuccess" });
      }
      if (localPreview) {
        try { URL.revokeObjectURL(localPreview); } catch (e) {}
        setLocalPreview(null);
      }
    }

    if (uploadImageError) {
      toast.error("File upload failed", { id: "uploadFileError" });
    }
  }, [uploadImageData, uploadImageError, uploadImageIsLoading]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const handleDenominationChange = (index, field, value) => {
    const updatedDenominations = formData.denominations.map((item, idx) => {
      if (idx === index) {
        return { ...item, [field]: field === "denomination" ? parseInt(value) || "" : parseFloat(value) || 0 };
      }
      return item;
    });
    setFormData(prev => ({
      ...prev,
      denominations: updatedDenominations
    }));
  };

  const addDenomination = () => {
    setFormData(prev => ({
      ...prev,
      denominations: [...prev.denominations, { denomination: "", value: 0 }]
    }));
  };

  const removeDenomination = (index) => {
    if (formData.denominations.length > 1) {
      setFormData(prev => ({
        ...prev,
        denominations: prev.denominations.filter((_, idx) => idx !== index)
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.brand_name.trim()) {
      newErrors.brand_name = "Brand Name is required";
    }

    // Validate min/max redemption amounts (if provided ensure numeric and min <= max)
    const minVal = formData.min_redemption_amount === "" ? null : Number(formData.min_redemption_amount);
    const maxVal = formData.max_redemption_amount === "" ? null : Number(formData.max_redemption_amount);
    if (minVal !== null && Number.isNaN(minVal)) {
      newErrors.min_redemption_amount = "Minimum redemption amount must be a number";
    }
    if (maxVal !== null && Number.isNaN(maxVal)) {
      newErrors.max_redemption_amount = "Maximum redemption amount must be a number";
    }
    if (minVal !== null && maxVal !== null && minVal > maxVal) {
      newErrors.max_redemption_amount = "Maximum must be greater than or equal to minimum";
    }

    // Validate denominations
    const validDenominations = formData.denominations.filter(d => d.denomination && d.denomination > 0);
    if (validDenominations.length === 0) {
      newErrors.denominations = "At least one valid denomination is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(errors);
      return;
    }

    // Create denomination_list string from denominations array
    const validDenominations = formData.denominations.filter(d => d.denomination && d.denomination > 0);
    const denominationList = validDenominations.map(d => d.denomination).join(",");

    const submitData = {
      ...formData,
      denomination_list: denominationList,
      value: validDenominations,
      min_redemption_amount: formData.min_redemption_amount === "" ? null : Number(formData.min_redemption_amount),
      max_redemption_amount: formData.max_redemption_amount === "" ? null : Number(formData.max_redemption_amount)
    };
    
    onSubmit(submitData);
  };

  const handleReset = () => {
    setFormData({
      brand_product_code: "",
      brand_name: "",
      brand_type: "",
      redemption_type: "",
      online_redemption_url: "",
      brand_image: "",
      min_redemption_amount: "",
      max_redemption_amount: "",
      category: "",
      descriptions: "",
      tnc: "",
      important_instruction: "",
      redeem_steps: "",
      stock_available: true,
      denomination_list: "",
      denominations: [{ denomination: "", value: 0 }]
    });
    setErrors({});
  };

  return (
    <div className="w-full p-4">
      <h3 className="text-xl font-semibold text-center mb-4">Add New Dynamic Coupon</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <InputComponent
              name="Coupon Provider Name"
              type="text"
              value={formData.brand_name}
              setValue={(value) => handleInputChange("brand_name", value)}
              placeholder="Enter brand name"
              required={true}
            />
            {errors.brand_name && (
              <p className="text-red-500 text-sm mt-1">{errors.brand_name}</p>
            )}
          </div>

          <div>
            <InputComponent
              name="Coupon Type"
              type="text"
              value={formData.brand_type}
              setValue={(value) => handleInputChange("brand_type", value)}
              placeholder="Enter brand type"
            />
          </div>

          {/* <div>
            <InputComponent
              name="Category"
              type="text"
              value={formData.category}
              setValue={(value) => handleInputChange("category", value)}
              placeholder="Enter category"
              required={true}
            />
            {errors.category && (
              <p className="text-red-500 text-sm mt-1">{errors.category}</p>
            )}
          </div> */}

          {/* <div>
            <InputComponent
              name="Redemption Type"
              type="text"
              value={formData.redemption_type}
              setValue={(value) => handleInputChange("redemption_type", value)}
              placeholder="Enter redemption type"
              required={true}
            />
            {errors.redemption_type && (
              <p className="text-red-500 text-sm mt-1">{errors.redemption_type}</p>
            )}
          </div> */}

          <div className="md:col-span-2">
            <label
              htmlFor="brand-file"
              className="flex flex-col items-center justify-center w-full h-36 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 p-3"
            >
              <div className="flex flex-col items-center justify-center">
                <svg
                  aria-hidden="true"
                  className="w-12 h-12 mb-2 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  ></path>
                </svg>
                <p className="text-sm text-gray-600">Click to upload JPEG or CSV file</p>
                <p className="text-xs text-gray-500">Accepted: .jpeg .jpg .csv</p>
              </div>
              <input
                id="brand-file"
                type="file"
                accept="image/jpeg,image/jpg,.csv"
                className="hidden"
                onChange={(e) => handleFileSelect(e)}
              />
            </label>

            {/* Preview area */}
            <div className="mt-2">
              {formData.brand_image && formData.brand_image.startsWith && formData.brand_image.startsWith("http") ? (
                // show preview if image
                <img
                  src={formData.brand_image}
                  alt="brand"
                  className="h-20 w-20 object-cover border border-gray-300 rounded-md"
                />
              ) : formData.brand_image ? (
                // show filename for csv or non-http values
                <div className="text-sm text-gray-700">{formData.brand_image}</div>
              ) : null}
            </div>
          </div>

          <div className="md:col-span-2">
            <InputComponent
              name="Online Redemption URL"
              type="url"
              value={formData.online_redemption_url}
              setValue={(value) => handleInputChange("online_redemption_url", value)}
              placeholder="https://redemption-url.com"
            />
          </div>

         {/* <div className="md:col-span-2">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="stock_available"
                checked={formData.stock_available}
                onChange={(e) => handleInputChange("stock_available", e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="stock_available" className="text-sm font-medium text-gray-700">
                Stock Available
              </label>
            </div>
          </div> */}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descriptions
          </label>
          <textarea
            value={formData.descriptions}
            onChange={(e) => handleInputChange("descriptions", e.target.value)}
            placeholder="Enter coupon descriptions"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Terms & Conditions
          </label>
          <textarea
            value={formData.tnc}
            onChange={(e) => handleInputChange("tnc", e.target.value)}
            placeholder="Enter terms and conditions"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Important Instructions
          </label>
          <textarea
            value={formData.important_instruction}
            onChange={(e) => handleInputChange("important_instruction", e.target.value)}
            placeholder="Enter important instructions"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Redeem Steps (JSON format)
          </label>
          <textarea
            value={formData.redeem_steps}
            onChange={(e) => handleInputChange("redeem_steps", e.target.value)}
            placeholder='{"step1": {"text": "Step 1 description", "image": "image_url"}}'
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>


        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Denominations *
            </label>
            <button
              type="button"
              onClick={addDenomination}
              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm"
            >
              <AiOutlinePlus className="w-4 h-4" />
              <span>Add Denomination</span>
            </button>
          </div>

          {errors.denominations && (
            <p className="text-red-500 text-sm mb-2">{errors.denominations}</p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Redemption Amount</label>
              <input
                type="number"
                step="any"
                value={formData.min_redemption_amount}
                onChange={(e) => handleInputChange("min_redemption_amount", e.target.value)}
                placeholder="Minimum redemption amount"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.min_redemption_amount && (
                <p className="text-red-500 text-sm mt-1">{errors.min_redemption_amount}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Redemption Amount</label>
              <input
                type="number"
                step="any"
                value={formData.max_redemption_amount}
                onChange={(e) => handleInputChange("max_redemption_amount", e.target.value)}
                placeholder="Maximum redemption amount"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.max_redemption_amount && (
                <p className="text-red-500 text-sm mt-1">{errors.max_redemption_amount}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            {formData.denominations.map((item, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className="flex-1">
                  <input
                    type="number"
                    placeholder="Denomination"
                    value={item.denomination}
                    onChange={(e) => handleDenominationChange(index, "denomination", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <input
                    type="number"
                    placeholder="Points Value"
                    value={item.value}
                    onChange={(e) => handleDenominationChange(index, "value", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {formData.denominations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDenomination(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <AiOutlineDelete className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {isLoading ? "Creating..." : "Create Coupon"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManualCouponForm;